import React from 'react';
import { alpha, Box, ButtonBase, Stack, Typography, useTheme } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import ChevronRightRounded from '@mui/icons-material/ChevronRightRounded';
import { createPortal } from 'react-dom';
import ViewColumnRounded from '@mui/icons-material/ViewColumnRounded';
import { ColumnPicker } from './ColumnPicker';
import { keepsItsCase } from './SortHeader';
import { PanelActions } from '../layout/Panel';
import { ScrollArea } from '../layout/ScrollArea';
import { EmptyState } from '../feedback/EmptyState';
import { InfoHint, type InfoHintProps } from './InfoHint';
import { isSurfaceClick } from '../utils/events';
import { useElementWidth } from '../hooks/useElementWidth';

export type RowTone = 'warning' | 'danger';

export interface DataTableColumn<R> {
  key: string;
  header: React.ReactNode;
  /**
   * The column's name in plain text, for the column picker — a heading that
   * is a sort button or carries an info icon cannot be read as a label.
   * Defaults to the header when it is a string, else the key.
   */
  name?: string;
  /** Never hidden by the column picker. Sticky columns are fixed anyway. */
  fixed?: boolean;
  render: (row: R) => React.ReactNode;
  /** Defaults to right for `numeric` columns, left otherwise. */
  align?: 'left' | 'right' | 'center';
  /** Lines digits up and right-aligns. */
  numeric?: boolean;
  /** Fixed width, px. Required for `sticky` columns. */
  width?: number;
  minWidth?: number;
  /** Stays in place while the rest scrolls. Only leading columns can be sticky. */
  sticky?: boolean;
  /** Secondary text. */
  muted?: boolean;
  /**
   * Its cells wrap onto more lines instead of widening the table — for a
   * column of sentences. Give it a `minWidth` so it never wraps to a sliver.
   */
  wrap?: boolean;
  /**
   * A heading over this column and its neighbours with the same group — a
   * month over its "Target" and "Excess" columns. Adds a second header row.
   */
  group?: string;
}

export interface DataTableGroup<R> {
  key: string;
  title: React.ReactNode;
  /** About the whole group, behind an info icon beside its title. */
  info?: React.ReactNode;
  infoTone?: InfoHintProps['tone'];
  rows: R[];
}

export interface DataTableProps<R> {
  columns: DataTableColumn<R>[];
  /** Rows in labelled groups. For an ungrouped table, pass one group with no title. */
  groups: DataTableGroup<R>[];
  getRowKey: (row: R) => string;
  /** A coloured edge on rows whose figures need a caveat. */
  rowTone?: (row: R) => RowTone | null;
  /**
   * One cell spanning from column `from` to the end, in place of those cells —
   * for a row that has a message rather than values.
   */
  renderRowSpan?: (row: R) => { from: string; content: React.ReactNode } | null;
  /**
   * Content opened under a row, across the whole table — a breakdown of one
   * of its figures, say. Kept in view however far the table has scrolled
   * sideways. Return null for a row with nothing open.
   */
  renderRowDetail?: (row: R) => React.ReactNode | null;
  /** Group headers open and close their rows. On by default. */
  collapsibleGroups?: boolean;
  /** Groups that start folded, when the table keeps its own state. */
  defaultCollapsedGroups?: string[];
  /**
   * Folded groups, when the page keeps the state — so it survives the table
   * being unmounted, e.g. behind a loading placeholder.
   */
  collapsedGroups?: string[];
  onCollapsedGroupsChange?: (keys: string[]) => void;
  /** Scroll vertically beyond this height, with the header pinned. */
  maxHeight?: number | string;
  /**
   * Take the height its container gives it and scroll the rows within that,
   * header pinned — for a table filling a pane rather than growing past it.
   */
  fill?: boolean;
  /** Names the table for screen readers. */
  ariaLabel: string;
  emptyMessage?: string;
  /**
   * A control for choosing which columns are shown. On by default once a
   * table has enough columns to need scrolling, off when a table is small
   * enough to read whole.
   *
   * The choice lives as long as the table is on screen and is never saved:
   * it is a way of looking at the figures, not a setting. Every column comes
   * back on a reload, and a download is of the data, not of the view.
   */
  columnPicker?: boolean;
}

/** Past this many columns a table is wide enough to want trimming. */
const PICKER_FROM = 8;

/**
 * The words in a heading, for the column picker.
 *
 * A heading is often a component — a sort button, a label beside an info icon
 * — so its text has to be read out of the node rather than off a string. The
 * two the kit builds headings with, `SortHeader` and a label/info pair, both
 * carry a `label`; anything else is walked for its children. A column can
 * always set `name` and skip this.
 */
function headingText(node: React.ReactNode): string | null {
  if (typeof node === 'string') return node;
  if (typeof node === 'number') return String(node);
  if (Array.isArray(node)) {
    const parts = node.map(headingText).filter(Boolean);
    return parts.length ? parts.join(' ') : null;
  }
  if (React.isValidElement(node)) {
    const props = node.props as { label?: unknown; children?: React.ReactNode };
    if (typeof props.label === 'string') return props.label;
    return headingText(props.children);
  }
  return null;
}

/**
 * A table for dense figures: many columns, rows in groups.
 *
 * - Leading columns can be sticky, so row names stay in view while periods
 *   scroll past; a shadow on their edge shows once the table has scrolled.
 * - Scrolls sideways inside a `ScrollArea`, which shows a shadow and an arrow
 *   on whichever side has more columns, so hidden columns are never a secret.
 * - Groups have a header that opens and closes its rows, with a row count and
 *   an info icon for anything that applies to the whole group. Group titles
 *   stay in view however far the table is scrolled.
 * - Numbers line up; rows highlight on hover.
 *
 * Explanations use `InfoHint` — in a cell renderer for a row, via `info` for
 * a group — the same as everywhere else. Presentation only: rows, columns and
 * what each cell shows come from the page.
 */
export function DataTable<R>({
  columns: allColumns,
  groups,
  getRowKey,
  rowTone,
  renderRowSpan,
  renderRowDetail,
  collapsibleGroups = true,
  defaultCollapsedGroups = [],
  collapsedGroups,
  onCollapsedGroupsChange,
  maxHeight,
  fill = false,
  ariaLabel,
  emptyMessage = 'Nothing to show for this selection.',
  columnPicker,
}: DataTableProps<R>) {
  const theme = useTheme();
  const [hidden, setHidden] = React.useState<string[]>([]);
  const [frameEl, setFrameEl] = React.useState<HTMLDivElement | null>(null);
  const showPicker = columnPicker ?? allColumns.length >= PICKER_FROM;
  // A panel that holds this table keeps space for controls like this one in
  // its header, beside Download and Expand, which is where a reader looks for
  // them. Without a panel around it the control sits above the table instead.
  const panel = React.useContext(PanelActions);
  // In a panel whose actions live behind a ⋯ menu, this one goes there too —
  // a widget header showing Columns beside a menu holding Download and Expand
  // put the same kind of control in two places.
  const [menuOpen, setMenuOpen] = React.useState(false);
  const inMenu = showPicker && panel.hasMenu && Boolean(panel.addMenuItem);
  React.useEffect(() => {
    if (!inMenu || !panel.addMenuItem) return undefined;
    return panel.addMenuItem({
      key: 'columns',
      label: hidden.length ? `Columns (${allColumns.length - hidden.length} of ${allColumns.length})` : 'Columns',
      icon: <ViewColumnRounded fontSize="small" />,
      onSelect: () => setMenuOpen(true),
    });
  }, [inMenu, panel.addMenuItem, hidden.length, allColumns.length]);
  // In the panel's toolbar row, which only shows when something is in it.
  const inSlot = showPicker && !inMenu && Boolean(panel.slot);
  React.useEffect(() => (inSlot && panel.occupy ? panel.occupy() : undefined), [inSlot, panel.occupy]);
  // Everything below works off the columns actually drawn, so group headings,
  // spans and sticky offsets follow a hidden column without knowing about it.
  const columns = showPicker ? allColumns.filter((c) => !hidden.includes(c.key)) : allColumns;
  // The visible width, so an opened row's content fits the view, not the
  // full scrolling width of every column.
  const [frameRef, frameWidth] = useElementWidth<HTMLDivElement>();
  // One stable callback: an inline ref is a different function on every
  // render, and React detaches and reattaches it each time.
  const setFrame = React.useCallback(
    (node: HTMLDivElement | null) => {
      frameRef(node);
      setFrameEl(node);
    },
    [frameRef],
  );
  // The header stays in view whenever the rows scroll inside the table.
  const pinHeader = maxHeight !== undefined || fill;
  const [ownCollapsed, setOwnCollapsed] = React.useState<string[]>(defaultCollapsedGroups);
  const collapsedKeys = collapsedGroups ?? ownCollapsed;
  const collapsed = new Set(collapsedKeys);

  const toggleGroup = (key: string) => {
    const next = collapsed.has(key) ? collapsedKeys.filter((k) => k !== key) : [...collapsedKeys, key];
    if (collapsedGroups === undefined) setOwnCollapsed(next);
    onCollapsedGroupsChange?.(next);
  };

  // Pinned offsets: each sticky column sits after the widths of those before it.
  const stickyLeft: (number | null)[] = [];
  let pinned = 0;
  let lastSticky = -1;
  columns.forEach((col, i) => {
    const contiguous = i === 0 || stickyLeft[i - 1] !== null;
    if (col.sticky && contiguous) {
      stickyLeft.push(pinned);
      pinned += col.width ?? 0;
      lastSticky = i;
    } else {
      stickyLeft.push(null);
    }
  });

  const headerHeight = theme.ds.sizes.control.md;
  const rowCount = groups.reduce((n, g) => n + g.rows.length, 0);
  if (!rowCount) return <EmptyState message={emptyMessage} height={160} />;

  const toneColor = (t: Theme, tone: RowTone | null) =>
    tone === 'danger' ? t.tokens.semantic.danger : tone === 'warning' ? t.tokens.semantic.warning : null;

  // Runs of neighbouring columns that share a group heading.
  const hasGroups = columns.some((c) => c.group);
  const groupSpans: { group?: string; start: number; length: number }[] = [];
  columns.forEach((col, i) => {
    const last = groupSpans[groupSpans.length - 1];
    if (col.group && last && last.group === col.group) last.length += 1;
    else groupSpans.push({ group: col.group, start: i, length: 1 });
  });

  /**
   * A column's `width` is a width, not a wish. In a table sized to its
   * container the browser treats a cell's width as a preference and will
   * squeeze the column down to its narrowest content — a campaign name that may
   * break anywhere collapses to one letter a line once a chat panel takes the
   * room. A block inside the cell with a min-width is honoured, so the column
   * holds and the table scrolls under its frozen columns instead.
   */
  const hold = (i: number, content: React.ReactNode) => {
    const width = columns[i].width;
    if (width === undefined) return content;
    // Less the cell's own side padding (see cellSx): 16px at the table's
    // outer edges, 12px elsewhere.
    const inset = (i === 0 ? 16 : 12) + (i === columns.length - 1 ? 16 : 12);
    return <Box sx={{ minWidth: Math.max(0, width - inset) }}>{content}</Box>;
  };

  const cellSx = (t: Theme, i: number, opts: { header?: boolean; tone?: RowTone | null; keepCase?: boolean } = {}) => {
    const col = columns[i];
    const left = stickyLeft[i];
    // A hairline before each group, so the months read as blocks.
    const startsGroup = Boolean(col.group) && (i === 0 || columns[i - 1].group !== col.group);
    const shadows: string[] = [];
    const rail = i === 0 ? toneColor(t, opts.tone ?? null) : null;
    if (rail) shadows.push(`inset 3px 0 0 ${rail}`);

    return {
      ...(opts.header ? t.typography.overline : t.typography.body2),
      ...(opts.header && opts.keepCase && { textTransform: 'none' as const }),
      boxSizing: 'border-box' as const,
      height: opts.header ? headerHeight : undefined,
      py: opts.header ? 0 : 0.75,
      pl: i === 0 ? 2 : 1.5,
      pr: i === columns.length - 1 ? 2 : 1.5,
      // A cell wraps when asked to, and whenever its column is held to a
      // width: text that does not fit would otherwise run into the next column.
      whiteSpace: !opts.header && (col.wrap || col.width) ? 'normal' as const : 'nowrap' as const,
      // A heading of two or more words wraps only when it has to: on a table
      // that fits, wrapped headings would waste a line for nothing. It has to
      // when the table is wider than its space, and when the column is held to
      // a width of its own — there the width is the limit, and a heading that
      // does not fit would otherwise be cut off.
      ...(opts.header && {
        ...(col.width ? { whiteSpace: 'normal' as const, lineHeight: 1.25 } : {}),
        '[data-overflowing="true"] &': { whiteSpace: 'normal' as const, lineHeight: 1.25 },
      }),
      verticalAlign: 'middle',
      textAlign: col.align ?? (col.numeric ? 'right' : 'left'),
      fontVariantNumeric: col.numeric ? 'tabular-nums' : undefined,
      color: opts.header || col.muted ? t.tokens.text.tertiary : t.tokens.text.primary,
      width: col.width,
      minWidth: col.width ?? col.minWidth,
      maxWidth: col.width,
      borderBottom: `1px solid ${opts.header ? t.tokens.border.default : t.tokens.border.subtle}`,
      ...(startsGroup && { borderLeft: `1px solid ${t.tokens.border.subtle}` }),
      bgcolor: opts.header || left !== null ? t.tokens.surface.raised : undefined,
      boxShadow: shadows.length ? shadows.join(', ') : undefined,
      ...(opts.header && pinHeader && {
        position: 'sticky' as const,
        top: hasGroups ? headerHeight : 0,
        zIndex: 2,
      }),
      ...(left !== null && {
        position: 'sticky' as const,
        left,
        zIndex: opts.header ? 3 : 1,
      }),
      ...(i === lastSticky && {
        borderRight: `1px solid ${t.tokens.border.subtle}`,
        transition: `box-shadow ${t.ds.motion.duration.normal}ms ${t.ds.motion.easing.standard}`,
        '[data-scrolled-start="true"] &': {
          boxShadow: [...shadows, `8px 0 8px -8px ${alpha(t.tokens.text.primary, 0.3)}`].join(', '),
        },
      }),
    };
  };

  return (
    <Box
      ref={setFrame}
      sx={fill ? { height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' } : undefined}>
    {showPicker && (() => {
      const pickerColumns = allColumns.map((c, i) => ({
        key: c.key,
        name: c.name ?? headingText(c.header) ?? c.key,
        fixed: Boolean(c.fixed || c.sticky) || i === 0,
      }));
      if (inMenu) {
        // Opened from the menu item above, over the table's top corner.
        return (
          <ColumnPicker
            columns={pickerColumns}
            hidden={hidden}
            onChange={setHidden}
            label={ariaLabel}
            open={menuOpen}
            onOpenChange={setMenuOpen}
            anchorEl={frameEl}
          />
        );
      }
      const picker = (
        <ColumnPicker
          columns={pickerColumns}
          hidden={hidden}
          onChange={setHidden}
          label={ariaLabel}
          roomy={panel.roomy}
        />
      );
      return panel.slot
        ? createPortal(picker, panel.slot)
        : <Box sx={{ display: 'flex', justifyContent: 'flex-end', pb: 0.5 }}>{picker}</Box>;
    })()}
    {/* With sticky columns the last one draws its own edge shadow, row by
        row, so the area's full-height shadow stays off: it would cross an
        opened row. */}
    <ScrollArea
      ariaLabel={ariaLabel}
      insetStart={pinned}
      noStartShade={pinned > 0}
      controlsTop={headerHeight / 2}
      maxHeight={maxHeight}
      fill={fill}
    >
      <Box
        component="table"
        aria-label={ariaLabel}
        sx={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}
      >
        <Box component="thead">
          {hasGroups && (
            <Box component="tr">
              {groupSpans.map((span) =>
                span.group ? (
                  <Box
                    component="th"
                    key={`group-${span.start}`}
                    scope="colgroup"
                    colSpan={span.length}
                    sx={(t) => ({
                      ...t.typography.subtitle2,
                      height: headerHeight,
                      px: 1.5,
                      textAlign: 'center',
                      whiteSpace: 'nowrap',
                      color: t.tokens.text.primary,
                      bgcolor: t.tokens.surface.raised,
                      borderBottom: `1px solid ${t.tokens.border.subtle}`,
                      borderLeft: `1px solid ${t.tokens.border.subtle}`,
                      ...(pinHeader && { position: 'sticky' as const, top: 0, zIndex: 2 }),
                    })}
                  >
                    {span.group}
                  </Box>
                ) : (
                  // A column outside any group — the row names — keeps its
                  // heading on the lower row; this cell just holds the place.
                  <Box
                    component="td"
                    key={`group-${span.start}`}
                    aria-hidden
                    sx={(t) => ({ ...cellSx(t, span.start, { header: true }), borderBottom: 0 })}
                  />
                ),
              )}
            </Box>
          )}
          <Box component="tr">
            {columns.map((col, i) => (
              <Box
                component="th"
                key={col.key}
                scope="col"
                sx={(t) => cellSx(t, i, {
                  header: true,
                  keepCase: keepsItsCase(col.name ?? headingText(col.header) ?? ''),
                })}
              >
                {hold(i, col.header)}
              </Box>
            ))}
          </Box>
        </Box>

        {groups.map((group) => {
          const isCollapsed = collapsed.has(group.key);
          const heading = (
            <>
              <Typography variant="overline" sx={(t) => ({ color: t.tokens.text.secondary })}>
                {group.title}
              </Typography>
              <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>
                {group.rows.length}
              </Typography>
            </>
          );

          return (
            <Box component="tbody" key={group.key}>
              {group.title && (
                <Box component="tr">
                  <Box
                    component="th"
                    scope="colgroup"
                    colSpan={columns.length}
                    // The whole row opens and closes the group, the same as a
                    // panel header; the chevron button stays for keyboards.
                    onClick={(e: React.MouseEvent<HTMLElement>) => {
                      if (collapsibleGroups && isSurfaceClick(e)) toggleGroup(group.key);
                    }}
                    sx={(t) => ({
                      p: 0,
                      textAlign: 'left',
                      bgcolor: t.tokens.surface.sunken,
                      borderBottom: `1px solid ${t.tokens.border.subtle}`,
                      ...(collapsibleGroups && {
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundImage: `linear-gradient(${t.tokens.brand.primarySofter}, ${t.tokens.brand.primarySofter})`,
                        },
                      }),
                    })}
                  >
                    {/* Pinned to the visible width, so the title stays where the
                        reader is looking however far the table has scrolled. */}
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{ position: 'sticky', left: 0, width: 'fit-content', px: 2, py: 0.75 }}
                    >
                      {collapsibleGroups ? (
                        <ButtonBase
                          onClick={() => toggleGroup(group.key)}
                          aria-expanded={!isCollapsed}
                          sx={(t) => ({
                            gap: 0.5,
                            ml: -0.5,
                            pr: 0.5,
                            borderRadius: `${t.ds.radius.xs}px`,
                            '&.Mui-focusVisible': { outline: `2px solid ${t.tokens.border.focus}` },
                          })}
                        >
                          <ChevronRightRounded
                            fontSize="small"
                            sx={(t) => ({
                              color: t.tokens.text.tertiary,
                              transform: isCollapsed ? 'none' : 'rotate(90deg)',
                              transition: `transform ${t.ds.motion.duration.fast}ms ${t.ds.motion.easing.standard}`,
                            })}
                          />
                          {heading}
                        </ButtonBase>
                      ) : (
                        heading
                      )}
                      {group.info && (
                        <InfoHint
                          title={group.info}
                          tone={group.infoTone}
                          label={typeof group.title === 'string' ? `About ${group.title}` : 'About this group'}
                        />
                      )}
                    </Stack>
                  </Box>
                </Box>
              )}

              {!isCollapsed &&
                group.rows.map((row) => {
                  const tone = rowTone ? rowTone(row) : null;
                  const span = renderRowSpan ? renderRowSpan(row) : null;
                  const spanFrom = span ? columns.findIndex((c) => c.key === span.from) : -1;
                  const detail = renderRowDetail ? renderRowDetail(row) : null;

                  return (
                    <React.Fragment key={getRowKey(row)}>
                    <Box
                      component="tr"
                      sx={(t) => ({
                        '&:hover > td': {
                          backgroundImage: `linear-gradient(${t.tokens.brand.primarySofter}, ${t.tokens.brand.primarySofter})`,
                        },
                      })}
                    >
                      {columns.map((col, i) => {
                        if (spanFrom >= 0 && i > spanFrom) return null;
                        if (span && i === spanFrom) {
                          return (
                            <Box
                              component="td"
                              key={col.key}
                              colSpan={columns.length - i}
                              sx={(t) => ({ ...cellSx(t, i, { tone }), textAlign: 'left', color: t.tokens.text.tertiary })}
                            >
                              {span.content}
                            </Box>
                          );
                        }
                        return (
                          <Box component="td" key={col.key} sx={(t) => cellSx(t, i, { tone })}>
                            {hold(i, col.render(row))}
                          </Box>
                        );
                      })}
                    </Box>
                    {detail && (
                      <Box component="tr">
                        <Box
                          component="td"
                          colSpan={columns.length}
                          sx={(t) => ({
                            p: 0,
                            bgcolor: t.tokens.surface.sunken,
                            borderBottom: `1px solid ${t.tokens.border.default}`,
                          })}
                        >
                          <Box
                            sx={{
                              position: 'sticky',
                              left: 0,
                              width: frameWidth ? `${frameWidth}px` : '100%',
                              maxWidth: '100%',
                              boxSizing: 'border-box',
                              whiteSpace: 'normal',
                              px: 2,
                              py: 1.5,
                            }}
                          >
                            {detail}
                          </Box>
                        </Box>
                      </Box>
                    )}
                    </React.Fragment>
                  );
                })}
            </Box>
          );
        })}
      </Box>
    </ScrollArea>
    </Box>
  );
}
